/**
 * CoachPlaybook - Dashboard Component
 * Version: 0.6.0
 * Description: ESPN broadcast-quality dashboard with sport selector, hero stats, quick actions, recent activity
 */

import React, { useState, useEffect } from 'react';
import { getStorageStats, getSetting, getPlayersBySport, getPlaysBySport, getTeamsBySport } from '../../utils/db';
import SportSelector from '../common/SportSelector';
import { useSport } from '../common/SportContext';
import { useTheme } from '../../utils/theme';

const FONT = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif";

/* ── SVG Icon Paths (ToolStrip-style, no emoji) ────────── */
const Icons = {
  playbook: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
      <path d="M13 8h4" />
      <path d="M13 12h4" />
      <path d="M13 16h2" />
    </svg>
  ),
  roster: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  learn: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
      <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
    </svg>
  ),
  stats: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 20V10" />
      <path d="M12 20V4" />
      <path d="M6 20v-6" />
    </svg>
  ),
  scouting: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
      <path d="M11 8v6" />
      <path d="M8 11h6" />
    </svg>
  ),
  settings: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  arrow: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="M12 5l7 7-7 7" />
    </svg>
  ),
  players: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    </svg>
  ),
  plays: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
    </svg>
  ),
  opponents: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
    </svg>
  ),
  clock: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
};

/* ── Hover animation helper (CSS-in-JS keyframes via style tag) ── */
const ANIM_ID = 'dashboard-anims';

const injectAnimations = () => {
  if (document.getElementById(ANIM_ID)) return;
  const style = document.createElement('style');
  style.id = ANIM_ID;
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700;800&display=swap');
    @keyframes dashFadeIn {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes dashPulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
    @keyframes dashSlideIn {
      from { opacity: 0; transform: translateX(20px); }
      to   { opacity: 1; transform: translateX(0); }
    }
  `;
  document.head.appendChild(style);
};

/* ── Utility: relative time ─────────────────────────────── */
const relativeTime = (timestamp) => {
  if (!timestamp) return '';
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

/* ══════════════════════════════════════════════════════════
   Dashboard Component
   ══════════════════════════════════════════════════════════ */
const Dashboard = ({ onNavigate, teamName, teams = [], currentTeamId, onTeamSwitch }) => {
  const [stats, setStats] = useState({ totalPlayers: 0, totalPlays: 0, opponents: 0 });
  const [recentPlays, setRecentPlays] = useState([]);
  const [teamLogo, setTeamLogo] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredStat, setHoveredStat] = useState(null);

  const { theme: themeColors } = useTheme();
  const C = {
    bg: themeColors.bgPanel,
    panel: themeColors.bg,
    card: themeColors.bgCard,
    border: themeColors.border,
    text: themeColors.text,
    textSecondary: themeColors.textSecondary,
    textMuted: themeColors.textMuted,
    glass: themeColors.glass,
    glassBorder: themeColors.glassBorder,
  };

  const { currentSport, currentAgeGroup, setSport, setAgeGroup, sportConfig } = useSport();
  const accent = sportConfig?.accentColor || '#f97316';

  useEffect(() => { injectAnimations(); }, []);

  useEffect(() => {
    const loadData = async () => {
      const [players, plays, opponentTeams] = await Promise.all([
        getPlayersBySport(currentSport),
        getPlaysBySport(currentSport),
        getTeamsBySport(currentSport)
      ]);
      setStats({
        totalPlayers: players.length,
        totalPlays: plays.length,
        opponents: opponentTeams.length
      });

      // Get recent plays sorted by updatedAt/createdAt
      const sorted = [...plays]
        .sort((a, b) => {
          const aTime = a.updatedAt || a.createdAt || 0;
          const bTime = b.updatedAt || b.createdAt || 0;
          return new Date(bTime) - new Date(aTime);
        })
        .slice(0, 8);
      setRecentPlays(sorted);

      const logo = await getSetting('teamLogo');
      if (logo) setTeamLogo(logo);
    };
    loadData();
  }, [currentTeamId, currentSport]);

  /* ── Action cards config ──────────────────────────────── */
  const actionCards = [
    {
      id: 'plays',
      label: 'Playbook Studio',
      subtitle: 'Design, animate & share plays',
      icon: Icons.playbook,
      nav: 'plays',
      primary: true,
    },
    {
      id: 'roster',
      label: 'Team Roster',
      subtitle: 'Manage players & positions',
      icon: Icons.roster,
      nav: 'roster',
    },
    {
      id: 'learn',
      label: 'Learn',
      subtitle: 'Drills & coaching guides',
      icon: Icons.learn,
      nav: 'learn',
    },
    {
      id: 'stats',
      label: 'Stats',
      subtitle: 'Track game performance',
      icon: Icons.stats,
      nav: 'stats',
    },
    {
      id: 'scouting',
      label: 'Scouting',
      subtitle: 'Scout opponent teams',
      icon: Icons.scouting,
      nav: 'scouting',
    },
    {
      id: 'settings',
      label: 'Settings',
      subtitle: 'Team & app configuration',
      icon: Icons.settings,
      nav: 'teamSettings',
    },
  ];

  /* ── Hero stat cards data ─────────────────────────────── */
  const heroStats = [
    { key: 'players', value: stats.totalPlayers, label: 'Players', icon: Icons.players, nav: 'roster' },
    { key: 'plays', value: stats.totalPlays, label: 'Plays', icon: Icons.plays, nav: 'plays' },
    { key: 'opponents', value: stats.opponents, label: 'Opponents', icon: Icons.opponents, nav: 'scouting' },
  ];

  /* ── Inline Styles ────────────────────────────────────── */
  const s = {
    root: {
      height: '100%',
      overflowY: 'auto',
      background: C.bg,
      fontFamily: FONT,
      color: C.text,
    },
    inner: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '24px 28px 80px',
    },

    /* Top Bar */
    topBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '16px',
      marginBottom: '28px',
      animation: 'dashFadeIn 0.4s ease-out',
    },
    topBarLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      flex: 1,
      minWidth: 0,
    },
    sportSelectorWrap: {
      flex: 1,
      minWidth: 0,
    },
    teamInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flexShrink: 0,
    },
    teamNameText: {
      fontSize: '14px',
      fontWeight: 600,
      color: C.textSecondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '160px',
    },
    teamSwitcher: {
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: '8px',
      padding: '4px 10px',
      fontSize: '12px',
      color: C.text,
      fontFamily: FONT,
      outline: 'none',
      cursor: 'pointer',
    },
    avatar: {
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      overflow: 'hidden',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `2px solid ${C.border}`,
    },
    avatarImg: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    avatarFallback: {
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      background: `linear-gradient(135deg, ${accent}, ${accent}99)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
      fontWeight: 800,
      color: '#fff',
      border: `2px solid ${accent}44`,
    },

    /* Hero Stats Row */
    heroRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '16px',
      marginBottom: '28px',
    },

    /* Quick Actions */
    sectionLabel: {
      fontSize: '11px',
      fontWeight: 700,
      color: C.textMuted,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      marginBottom: '14px',
      fontFamily: FONT,
    },
    actionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '14px',
      marginBottom: '32px',
    },

    /* Recent Activity */
    recentScroll: {
      display: 'flex',
      gap: '14px',
      overflowX: 'auto',
      paddingBottom: '8px',
      scrollbarWidth: 'thin',
      scrollbarColor: `${C.border} transparent`,
    },
  };

  /* ── Hero Stat Card ───────────────────────────────────── */
  const renderHeroStat = (stat, index) => {
    const isHovered = hoveredStat === stat.key;
    return (
      <div
        key={stat.key}
        data-element={`dashboard-hero-${stat.key}`}
        onClick={() => stat.nav && onNavigate(stat.nav)}
        onMouseEnter={() => setHoveredStat(stat.key)}
        onMouseLeave={() => setHoveredStat(null)}
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '16px',
          padding: '24px',
          background: `linear-gradient(135deg, ${accent}18, ${accent}08)`,
          border: `1px solid ${isHovered ? `${accent}66` : C.glassBorder}`,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          cursor: 'pointer',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isHovered ? 'translateY(-3px) scale(1.02)' : 'translateY(0) scale(1)',
          boxShadow: isHovered ? `0 12px 32px ${accent}22` : '0 4px 16px rgba(0,0,0,0.2)',
          animation: `dashFadeIn 0.5s ease-out ${index * 0.1}s both`,
        }}
      >
        {/* Gradient accent bar at top */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${accent}, ${accent}44)`,
          borderRadius: '16px 16px 0 0',
        }} />
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{
              fontSize: '44px',
              fontWeight: 800,
              color: C.text,
              lineHeight: 1,
              marginBottom: '6px',
              fontFamily: FONT,
            }}>
              {stat.value}
            </div>
            <div style={{
              fontSize: '13px',
              fontWeight: 600,
              color: C.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>
              {stat.label}
            </div>
          </div>
          <div style={{ color: `${accent}88`, opacity: 0.7 }}>
            {stat.icon}
          </div>
        </div>
      </div>
    );
  };

  /* ── Action Card ──────────────────────────────────────── */
  const renderActionCard = (card, index) => {
    const isHovered = hoveredCard === card.id;
    const isPrimary = card.primary;
    return (
      <button
        key={card.id}
        data-element={`dashboard-action-${card.id}`}
        onClick={() => onNavigate(card.nav)}
        onMouseEnter={() => setHoveredCard(card.id)}
        onMouseLeave={() => setHoveredCard(null)}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          textAlign: 'left',
          padding: '22px',
          borderRadius: '14px',
          border: `1px solid ${isHovered ? `${accent}55` : C.glassBorder}`,
          background: isPrimary
            ? (isHovered ? `linear-gradient(135deg, ${accent}30, ${accent}12)` : `linear-gradient(135deg, ${accent}20, ${accent}08)`)
            : (isHovered ? `rgba(30, 41, 59, 0.85)` : C.glass),
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          cursor: 'pointer',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
          boxShadow: isHovered
            ? `0 16px 40px rgba(0,0,0,0.3), 0 0 0 1px ${accent}22`
            : '0 2px 8px rgba(0,0,0,0.15)',
          outline: 'none',
          fontFamily: FONT,
          color: C.text,
          minHeight: isPrimary ? '140px' : '120px',
          gridColumn: isPrimary ? 'span 1' : undefined,
          animation: `dashFadeIn 0.5s ease-out ${0.15 + index * 0.06}s both`,
        }}
      >
        {/* Icon */}
        <div style={{
          width: '42px',
          height: '42px',
          borderRadius: '12px',
          background: isPrimary ? `${accent}25` : 'rgba(148, 163, 184, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isPrimary ? accent : C.textSecondary,
          marginBottom: '14px',
          transition: 'all 0.2s ease',
          ...(isHovered && { background: isPrimary ? `${accent}35` : 'rgba(148, 163, 184, 0.2)' }),
        }}>
          {card.icon}
        </div>

        {/* Text */}
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '15px',
            fontWeight: 700,
            color: C.text,
            marginBottom: '4px',
            lineHeight: 1.3,
          }}>
            {card.label}
          </div>
          <div style={{
            fontSize: '12px',
            color: C.textMuted,
            lineHeight: 1.4,
          }}>
            {card.subtitle}
          </div>
        </div>

        {/* Arrow */}
        <div style={{
          position: 'absolute',
          bottom: '18px',
          right: '18px',
          color: isHovered ? accent : C.textMuted,
          transition: 'all 0.2s ease',
          transform: isHovered ? 'translateX(3px)' : 'translateX(0)',
          opacity: isHovered ? 1 : 0.5,
        }}>
          {Icons.arrow}
        </div>
      </button>
    );
  };

  /* ── Recent Play Card ─────────────────────────────────── */
  const renderRecentPlay = (play, index) => (
    <div
      key={play.id || index}
      data-element={`dashboard-recent-${play.id || index}`}
      onClick={() => onNavigate('plays')}
      style={{
        minWidth: '200px',
        maxWidth: '240px',
        padding: '16px',
        borderRadius: '12px',
        background: C.glass,
        border: `1px solid ${C.glassBorder}`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        flexShrink: 0,
        animation: `dashSlideIn 0.4s ease-out ${index * 0.07}s both`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `${accent}55`;
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 8px 24px rgba(0,0,0,0.25)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = C.glassBorder;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{
        fontSize: '14px',
        fontWeight: 600,
        color: C.text,
        marginBottom: '8px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {play.name || 'Untitled Play'}
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '11px',
        color: C.textMuted,
      }}>
        <span style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: accent,
          flexShrink: 0,
        }} />
        <span>{sportConfig?.name || 'Sport'}</span>
        {(play.updatedAt || play.createdAt) && (
          <>
            <span style={{ color: C.border }}>|</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              {Icons.clock}
              {relativeTime(play.updatedAt || play.createdAt)}
            </span>
          </>
        )}
      </div>
    </div>
  );

  /* ── Render ───────────────────────────────────────────── */
  return (
    <div data-element="dashboard-root" style={s.root}>
      <div style={s.inner}>

        {/* ── Top Bar ──────────────────────────────────── */}
        <div data-element="dashboard-topbar" style={s.topBar}>
          <div style={s.topBarLeft}>
            <div style={s.sportSelectorWrap}>
              <SportSelector
                selectedSport={currentSport}
                selectedAgeGroup={currentAgeGroup}
                onSportChange={setSport}
                onAgeGroupChange={setAgeGroup}
                compact
              />
            </div>
          </div>
          <div style={s.teamInfo}>
            {teams.length > 1 && (
              <select
                data-element="dashboard-team-switcher"
                value={currentTeamId || ''}
                onChange={(e) => onTeamSwitch && onTeamSwitch(e.target.value)}
                style={s.teamSwitcher}
              >
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            )}
            <div style={s.teamNameText}>
              {teamName || 'CoachPlaybook'}
            </div>
            {teamLogo ? (
              <div style={s.avatar}>
                <img src={teamLogo} alt="Team" style={s.avatarImg} />
              </div>
            ) : (
              <div style={s.avatarFallback}>
                {(teamName || 'C')[0].toUpperCase()}
              </div>
            )}
          </div>
        </div>

        {/* ── Hero Stats Row ───────────────────────────── */}
        <div data-element="dashboard-hero-stats" style={s.heroRow}>
          {heroStats.map((stat, i) => renderHeroStat(stat, i))}
        </div>

        {/* ── Quick Actions ────────────────────────────── */}
        <div data-element="dashboard-actions-label" style={s.sectionLabel}>
          Quick Actions
        </div>
        <div data-element="dashboard-actions" style={s.actionsGrid}>
          {actionCards.map((card, i) => renderActionCard(card, i))}
        </div>

        {/* ── Recent Activity ──────────────────────────── */}
        {recentPlays.length > 0 && (
          <div data-element="dashboard-recent" style={{ animation: 'dashFadeIn 0.6s ease-out 0.5s both' }}>
            <div style={s.sectionLabel}>
              Recent Activity
            </div>
            <div style={s.recentScroll}>
              {recentPlays.map((play, i) => renderRecentPlay(play, i))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;
